import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabase';

// Thin banner that pins to the very top of the app for any signed-in user
// whose email Supabase hasn't confirmed yet. A single CTA re-sends the
// verification link; a less-prominent "dismiss for now" stashes a flag in
// sessionStorage so the banner stays hidden until next browser tab open.
const DISMISS_KEY = 'verify_email_dismissed';

export default function VerifyEmailBanner() {
  const { user } = useAuth();
  const toast = useToast();
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  // Nothing to show if the user either isn't signed in or is already verified.
  if (!user || user.email_confirmed_at) return null;
  if (dismissed) return null;

  async function resend() {
    if (!supabase || !user?.email) return;
    setSending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
      });
      if (error) throw error;
      toast?.success?.(`Verification email sent to ${user.email}.`);
    } catch (err) {
      toast?.error?.(err?.message || 'Could not send. Try again in a minute.');
    } finally {
      setSending(false);
    }
  }

  function dismiss() {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch {}
  }

  return (
    <div className="verify-email-banner" role="status">
      <span className="verify-email-banner-icon" aria-hidden>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16v16H4z"/>
          <polyline points="4 7 12 13 20 7"/>
        </svg>
      </span>
      <span className="verify-email-banner-text">
        Verify <strong>{user.email}</strong> to unlock pitching, booking, and
        verified creator status.
      </span>
      <div className="verify-email-banner-actions">
        <button
          type="button"
          className="verify-email-banner-btn"
          onClick={resend}
          disabled={sending}
        >
          {sending ? 'Sending\u2026' : 'Resend link'}
        </button>
        <button
          type="button"
          className="verify-email-banner-close"
          onClick={dismiss}
          aria-label="Dismiss for now"
          title="Dismiss until next session"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
